/*
 * Réglages de la domiciliation SEPA (crm_settings, clé « sepa ») : créancier, format du fichier XML,
 * plafonds de l'annexe technique BNP Paribas Fortis et délais de prélèvement.
 * La fonction create-sepa-batch relit ces réglages côté serveur.
 */
import { supabase } from '../../lib/supabase';
import {
  type SepaFormat, SEPA_FORMATS, ibanError, isValidBic, isValidCreditorId, normalizeBic, normalizeIban,
} from '../../../supabase/functions/_shared/sepa';

export interface SepaSettings {
  creditorName: string;
  creditorAddress: string;
  creditorCompanyNumber: string;
  /** Identifiant créancier SEPA (ex. BE76ZZZ1022108004). */
  creditorId: string;
  creditorIban: string;
  creditorBic: string;
  format: SepaFormat;
  /** Montant maximum d'un prélèvement (EUR). */
  maxAmount: number;
  /** Total maximum des prélèvements d'un même mois (EUR), selon la date de prélèvement. */
  maxMonthlyTotal: number;
  /** Délai minimum entre l'envoi de la facture et le prélèvement (jours ouvrables TARGET2). */
  minBusinessDays: number;
  /** Délai maximum (jours calendrier). */
  maxCalendarDays: number;
  /** Heure limite d'import dans Easy Banking Business, la veille ouvrée du prélèvement. */
  cutoffHour: number;
}

export const DEFAULT_SEPA_SETTINGS: SepaSettings = {
  creditorName: 'Sun Is Up ASBL',
  creditorAddress: 'Chée d’Ixelles 235, 1050 Bruxelles',
  creditorCompanyNumber: 'BE1022108004',
  creditorId: 'BE76ZZZ1022108004',
  creditorIban: 'BE96002011926005',
  creditorBic: 'GEBABEBB',
  format: 'pain.008.001.08',
  maxAmount: 2500,
  maxMonthlyTotal: 10000,
  minBusinessDays: 5,
  maxCalendarDays: 30,
  cutoffHour: 13,
};

const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const str = (v: unknown, fallback: string) => (typeof v === 'string' && v.trim() ? v.trim() : fallback);

export function normalizeSepaSettings(v: Partial<SepaSettings> | null | undefined): SepaSettings {
  const d = DEFAULT_SEPA_SETTINGS;
  return {
    creditorName: str(v?.creditorName, d.creditorName),
    creditorAddress: str(v?.creditorAddress, d.creditorAddress),
    creditorCompanyNumber: str(v?.creditorCompanyNumber, d.creditorCompanyNumber),
    creditorId: str(v?.creditorId, d.creditorId),
    creditorIban: normalizeIban(str(v?.creditorIban, d.creditorIban)),
    creditorBic: normalizeBic(str(v?.creditorBic, d.creditorBic)),
    format: SEPA_FORMATS.includes(v?.format as SepaFormat) ? v!.format as SepaFormat : d.format,
    maxAmount: num(v?.maxAmount, d.maxAmount),
    maxMonthlyTotal: num(v?.maxMonthlyTotal, d.maxMonthlyTotal),
    minBusinessDays: num(v?.minBusinessDays, d.minBusinessDays),
    maxCalendarDays: num(v?.maxCalendarDays, d.maxCalendarDays),
    cutoffHour: num(v?.cutoffHour, d.cutoffHour),
  };
}

/** Erreurs par champ (vide = réglages valides). */
export function sepaSettingsErrors(v: SepaSettings): Partial<Record<keyof SepaSettings, string>> {
  const e: Partial<Record<keyof SepaSettings, string>> = {};
  if (!v.creditorName.trim()) e.creditorName = 'Nom requis';
  const ibanErr = ibanError(v.creditorIban);
  if (ibanErr) e.creditorIban = ibanErr;
  if (!isValidBic(v.creditorBic)) e.creditorBic = 'BIC invalide';
  if (!isValidCreditorId(v.creditorId)) e.creditorId = 'Identifiant créancier invalide (clé de contrôle)';
  if (!(v.maxAmount > 0)) e.maxAmount = 'Montant positif requis';
  if (!(v.maxMonthlyTotal >= v.maxAmount)) e.maxMonthlyTotal = 'Au moins le montant maximum d’un prélèvement';
  if (!(Number.isInteger(v.minBusinessDays) && v.minBusinessDays >= 1)) e.minBusinessDays = 'Nombre entier d’au moins 1';
  if (!(Number.isInteger(v.maxCalendarDays) && v.maxCalendarDays >= 7)) e.maxCalendarDays = 'Nombre entier d’au moins 7';
  if (!(Number.isInteger(v.cutoffHour) && v.cutoffHour >= 0 && v.cutoffHour <= 23)) e.cutoffHour = 'Heure entre 0 et 23';
  return e;
}

let cache: SepaSettings | null = null;

export async function fetchSepaSettings(force = false): Promise<SepaSettings> {
  if (cache && !force) return cache;
  const { data, error } = await supabase.from('crm_settings').select('value').eq('key', 'sepa').maybeSingle();
  if (error) throw error;
  cache = normalizeSepaSettings((data as { value?: Partial<SepaSettings> } | null)?.value);
  return cache;
}

export async function saveSepaSettings(value: SepaSettings): Promise<SepaSettings> {
  const clean = normalizeSepaSettings(value);
  const errors = sepaSettingsErrors(clean);
  const first = Object.values(errors)[0];
  if (first) throw new Error(first);
  const { error } = await supabase.from('crm_settings')
    .upsert({ key: 'sepa', value: clean, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
  cache = clean;
  return clean;
}
